import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import { useEffect, useState } from "react";
import { Icons, Colors } from "utils/Constants";
import { Icon, IconButton, Popover } from "@mui/material";
import PropTypes from "prop-types";
import { AdvancedColorPickerContent } from "components/AdvancedColorPicker/AdvancedColorPicker";

function SortOrderHeader({ handleScopeSort, sorted }) {
  return (
    <div
      onClick={handleScopeSort}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && handleScopeSort()}
      style={{ cursor: "pointer" }}
    >
      Sort Order
      <MDBox
        position="absolute"
        top={-3}
        left="100%"
        color={sorted === "asc" ? "text" : "secondary"}
        opacity={sorted === "asc" ? 1 : 0.5}
      >
        <Icon fontSize="medium">arrow_drop_up</Icon>
      </MDBox>
      <MDBox
        position="absolute"
        top={3}
        left="100%"
        color={sorted === "desc" ? "text" : "secondary"}
        opacity={sorted === "desc" ? 1 : 0.5}
      >
        <Icon fontSize="medium">arrow_drop_down</Icon>
      </MDBox>
    </div>
  );
}

SortOrderHeader.propTypes = {
  handleScopeSort: PropTypes.func.isRequired,
  sorted: PropTypes.string.isRequired,
};

export default function Scopes(
  scopeList,
  handleOpenNewModal,
  setModalType,
  editLists,
  setEditLists,
  handleDelete,
  handleScopeSort,
  sorted,
  permission,
  handleColorUpdate
) {
  const [rows, setRows] = useState([]);
  const [colorPopover, setColorPopover] = useState({ open: false, anchorEl: null, scopeId: null });
  const mongooseId = "_id";

  const handleEdit = (item) => {
    setModalType("Update");
    setEditLists({ ...editLists, scope: item });
    handleOpenNewModal("Scopes");
  };

  const handleColorClick = (event, scopeId) => {
    setColorPopover({ open: true, anchorEl: event.currentTarget, scopeId });
  };

  const handleColorClose = () => {
    setColorPopover({ open: false, anchorEl: null, scopeId: null });
  };

  const handleColorSelect = (color, scopeItem, shouldClose = false) => {
    handleColorUpdate(scopeItem[mongooseId], color);
    if (shouldClose) {
      handleColorClose();
    }
  };

  useEffect(() => {
    if (scopeList) {
      const { scopeList: scopes } = { scopeList };
      const list = scopes.map((item) => {
        const temp = {
          name: (
            <MDTypography variant="caption" color="text" fontWeight="medium">
              {item.name}
            </MDTypography>
          ),
          color: (
            <MDBox sx={{ display: "flex", alignItems: "center" }}>
              {permission?.update ? (
                <MDBox
                  sx={{
                    width: 24,
                    height: 24,
                    backgroundColor: item.colorCode || "#f5f5f5",
                    borderRadius: "4px",
                    border: "1px solid #e0e0e0",
                    cursor: "pointer",
                    "&:hover": {
                      boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                    },
                  }}
                  onClick={(e) => handleColorClick(e, item[mongooseId])}
                />
              ) : (
                item.colorCode && (
                  <MDBox
                    sx={{
                      width: 24,
                      height: 24,
                      backgroundColor: item.colorCode,
                      borderRadius: "4px",
                      border: "1px solid #e0e0e0",
                    }}
                  />
                )
              )}
            </MDBox>
          ),
          isDoable: (
            <MDTypography variant="caption" color="text" fontWeight="medium">
              {item.isDoable}
            </MDTypography>
          ),
          sortOrder: (
            <MDBox sx={{ width: "max-content" }}>
              <MDTypography variant="caption" color="text" fontWeight="medium">
                {item.sortOrder}
              </MDTypography>
            </MDBox>
          ),
          action: (
            <MDBox>
              {permission?.update && (
                <IconButton
                  color="secondary"
                  fontSize="medium"
                  onClick={() => handleEdit(item)}
                  sx={{ cursor: "pointer" }}
                  disabled={!item?.isDeletable || false}
                >
                  {Icons.EDIT}
                </IconButton>
              )}{" "}
              &nbsp;
              {permission?.delete && (
                <IconButton
                  color="secondary"
                  fontSize="medium"
                  onClick={() => handleDelete("Scope", item[mongooseId])}
                  disabled={!item?.isDeletable || false}
                >
                  {Icons.DELETE}
                </IconButton>
              )}
            </MDBox>
          ),
        };
        return temp;
      });
      setRows([...list]);
    }
  }, [scopeList, permission]);

  return {
    ScopesColumns: [
      { Header: "Name", accessor: "name", width: "50%", align: "left" },
      { Header: "Color", accessor: "color", width: "20%", align: "left" },
      {
        // eslint-disable-next-line react/no-unstable-nested-components
        Header: () => <SortOrderHeader handleScopeSort={handleScopeSort} sorted={sorted} />,
        accessor: "sortOrder",
        width: "20%",
        align: "left",
      },
      ...(permission?.update || permission?.delete
        ? [{ Header: "Action", accessor: "action", width: "10%", align: "right" }]
        : []),
    ],
    ScopesRows: rows,
    ColorPopover: (
      <Popover
        open={colorPopover.open}
        anchorEl={colorPopover.anchorEl}
        onClose={handleColorClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            backgroundColor: Colors.WHITE,
            boxShadow: "0px 4px 12px rgba(0,0,0,0.15)",
            borderRadius: "8px",
            overflow: "visible",
            zIndex: 1500,
          },
        }}
      >
        <MDBox sx={{ p: 2 }}>
          <MDTypography variant="caption" fontWeight="medium" mb={1}>
            Select Color
          </MDTypography>
          {(() => {
            const { scopeList: scopes } = { scopeList };
            const scopeItem = scopes.find((item) => item[mongooseId] === colorPopover.scopeId);
            return (
              <AdvancedColorPickerContent
                colors={Colors.SCOPE_COLORS}
                selectedColor={scopeItem?.colorCode}
                onSelect={(color) => handleColorSelect(color, scopeItem, false)}
                onGridSelect={(color) => handleColorSelect(color, scopeItem, true)}
                size={24}
              />
            );
          })()}
        </MDBox>
      </Popover>
    ),
  };
}
